import React, { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { ShieldCheck, Download, RefreshCw, Loader2, Lock, FileText, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuditoriaResumen from "./AuditoriaResumen";
import { RoadmapLink } from "@/components/RoadmapLink";
import AuditoriaPlan from "./AuditoriaPlan";
import AuditoriaAnexo from "./AuditoriaAnexo";

const VIEWER_ROLES = new Set(["dir_admin_general", "socio_director", "dir_operaciones"]);

function Restricted() {
  return (
    <Card data-testid="auditoria-restricted">
      <CardContent className="flex flex-col items-center gap-3 p-14 text-center">
        <Lock className="h-10 w-10 text-muted-foreground/50" />
        <p className="font-medium">Sección reservada a Dirección</p>
        <p className="max-w-md text-sm text-muted-foreground">La auditoría de la plataforma contiene información de seguridad y datos internos. Solo pueden consultarla el Director-Administrador, los Socios y la Dirección de Operaciones.</p>
      </CardContent>
    </Card>
  );
}

export default function AuditoriaPlataformaPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("resumen");
  const [downloading, setDownloading] = useState(false);
  const allowed = !!user && (user.is_admin || VIEWER_ROLES.has(user.role));

  const q = useQuery({
    queryKey: ["plataforma-auditoria"],
    queryFn: async () => (await api.get("/plataforma-auditoria")).data,
    enabled: allowed,
    retry: false,
  });

  const republish = useMutation({
    mutationFn: async () => (await api.post("/plataforma-auditoria/publish")).data,
    onSuccess: (d) => { toast.success(`Auditoría republicada · versión ${d.version}`); qc.invalidateQueries({ queryKey: ["plataforma-auditoria"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/plataforma-auditoria/pdf", { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SOFI2_Auditoria_plataforma_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF generado");
    } catch (e) {
      toast.error(apiErrorMessage(e, "No se pudo generar el PDF"));
    } finally {
      setDownloading(false);
    }
  };

  if (!allowed || q.error?.response?.status === 403) return <Restricted />;
  if (q.isLoading) return <div className="space-y-4" data-testid="auditoria-loading"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (q.error) return <Card><CardContent className="p-8 text-sm text-destructive" data-testid="auditoria-error">{apiErrorMessage(q.error)}</CardContent></Card>;

  const data = q.data;
  const annexes = data.annexes || [];
  const publishedAt = data.published_at ? new Date(data.published_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="space-y-5" data-testid="auditoria-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <ShieldCheck className="h-6 w-6 text-primary" />Auditoría de la plataforma
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Estado real de SOFI 2.0, riesgos y plan de acción para abrir la plataforma a 20 usuarios. Auditoría de {data.audit_date_label}.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" data-testid="auditoria-version">Versión {data.version}</Badge>
            <span>Publicada el {publishedAt}</span>
            <Badge variant="secondary" className="gap-1"><Lock className="h-3 w-3" />Solo Dirección</Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <RoadmapLink />
          {data.can_publish && (
            <Button variant="outline" size="sm" onClick={() => republish.mutate()} disabled={republish.isPending} data-testid="auditoria-republish-btn">
              {republish.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}Republicar
            </Button>
          )}
          <Button size="sm" onClick={downloadPdf} disabled={downloading} data-testid="auditoria-download-pdf-btn">
            {downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Descargar todo (PDF)
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start gap-1" data-testid="auditoria-tabs">
          <TabsTrigger value="resumen" data-testid="auditoria-tab-resumen"><LayoutDashboard className="mr-1.5 h-3.5 w-3.5" />Resumen para dirección</TabsTrigger>
          {annexes.map((a) => (
            <TabsTrigger key={a.key} value={a.key} data-testid={`auditoria-tab-${a.key}`}><FileText className="mr-1.5 h-3.5 w-3.5" />{a.title}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "resumen" ? (
        <div className="space-y-8">
          <AuditoriaResumen summary={data.summary} onOpenAnnex={setTab} />
          <AuditoriaPlan plan={data.summary.plan} riesgos={data.summary.riesgos} decisions={data.decisions || {}} />
          <Glosario items={data.summary.glosario} />
        </div>
      ) : (
        <AuditoriaAnexo annexes={annexes} activeKey={tab} onSwitch={setTab} />
      )}
    </div>
  );
}

function Glosario({ items }) {
  return (
    <section data-testid="auditoria-glosario">
      <h2 className="mb-3 font-heading text-lg font-bold">Glosario · los términos que aparecen, en una línea</h2>
      <Card><CardContent className="grid gap-x-8 gap-y-2 p-5 sm:grid-cols-2">
        {items.map((g) => (
          <div key={g.termino} className="text-sm"><span className="font-semibold">{g.termino}</span><span className="text-muted-foreground"> — {g.definicion}</span></div>
        ))}
      </CardContent></Card>
    </section>
  );
}
